import React, { useState, useMemo } from 'react';
import { format } from 'date-fns';
import SaveInquiryModal from './SaveInquiryModal';
import { apiService, addObjection, addFAQ } from '../services/apiService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

interface InquiryData {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  userId: string;
  callStartTime: string;
  inquiry: {
    question: string;
    answer?: string;
    category: string;
    timestamp: string;
    relevance_score: number;
    isProcessed: boolean;
  };
}

interface NewInquiriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  inquiries: InquiryData[];
  loading?: boolean;
  onInquiryProcessed: () => void; // Callback to refresh analytics
}

const NewInquiriesModal: React.FC<NewInquiriesModalProps> = ({
  isOpen,
  onClose,
  inquiries,
  loading = false,
  onInquiryProcessed
}) => {
  const { isAdmin } = useRoleAccess();
  const [selectedSessionId, setSelectedSessionId] = useState<string>('all');
  const [showSaveModal, setShowSaveModal] = useState<InquiryData | null>(null);
  const [savingInquiry, setSavingInquiry] = useState<string | null>(null);
  const [processedInquiries, setProcessedInquiries] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Group inquiries by session
  const sessionGroups = useMemo(() => {
    const groups = new Map<string, {
      sessionId: string;
      sessionName: string;
      userEmail: string;
      callStartTime: string;
      inquiries: InquiryData[];
    }>();

    inquiries.forEach(item => {
      if (!groups.has(item.sessionId)) {
        groups.set(item.sessionId, {
          sessionId: item.sessionId,
          sessionName: item.sessionName,
          userEmail: item.userEmail,
          callStartTime: item.callStartTime,
          inquiries: []
        });
      }
      groups.get(item.sessionId)!.inquiries.push(item);
    });

    return Array.from(groups.values()).sort((a, b) =>
      new Date(b.callStartTime).getTime() - new Date(a.callStartTime).getTime()
    );
  }, [inquiries]);

  // Filter inquiries by selected session
  const filteredInquiries = useMemo(() => {
    if (selectedSessionId === 'all') {
      return inquiries;
    }
    return inquiries.filter(item => item.sessionId === selectedSessionId);
  }, [inquiries, selectedSessionId]);

  const handleMarkAsProcessed = async (inquiry: InquiryData) => {
    // Confirm with user
    const confirmed = window.confirm(
      'Are you sure you want to mark this inquiry as processed WITHOUT adding it to the knowledge base?\n\n' +
      'This will hide the inquiry from your unprocessed list, but it will NOT be saved to the FAQs or Objections database.'
    );

    if (!confirmed) {
      return;
    }

    try {
      await apiService.put(`/admin/sessions/${inquiry.sessionId}/inquiry-processed`, {
        question: inquiry.inquiry.question
      });

      // Update UI
      setProcessedInquiries(prev => new Set([...prev, inquiry.inquiry.question]));
      onInquiryProcessed(); // Refresh analytics count
      setSuccess('Inquiry marked as processed');
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      console.error('Failed to mark as processed:', err);
      setError('Failed to mark inquiry as processed');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSaveNewInquiry = async (
    inquiry: InquiryData,
    type: 'objection' | 'faq',
    editedData: { question: string; answer: string; category: string }
  ) => {
    try {
      setSavingInquiry(inquiry.inquiry.question);
      setError(null);

      // First, save to the dictionary (objection or FAQ)
      if (type === 'objection') {
        await addObjection(editedData.question, editedData.answer, editedData.category, true, null);
      } else {
        await addFAQ(editedData.question, editedData.answer, editedData.category);
      }

      // Show success message
      setSuccess(`Successfully added to ${type === 'objection' ? 'objections' : 'FAQs'} database`);
      setTimeout(() => setSuccess(null), 8000);

      // Then, mark as processed
      try {
        await apiService.put(`/admin/sessions/${inquiry.sessionId}/inquiry-processed`, {
          question: inquiry.inquiry.question
        });

        // Update UI
        setProcessedInquiries(prev => new Set([...prev, inquiry.inquiry.question]));
        onInquiryProcessed(); // Refresh analytics count
      } catch (err: any) {
        console.error('Failed to persist processed state:', err);
        setSuccess(prev => prev + ' (⚠️ Note: Could not mark as processed in session)');
      }

      setShowSaveModal(null);

      // Scroll to top to show success message
      const modalContent = document.getElementById('new-inquiries-modal-content');
      if (modalContent) {
        modalContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error: any) {
      console.error('Failed to save inquiry:', error);
      setError(error.message || 'Failed to save inquiry to database');
      setTimeout(() => setError(null), 5000);
      throw error; // Re-throw to let SaveInquiryModal handle it
    } finally {
      setSavingInquiry(null);
    }
  };

  if (!isOpen) return null;

  const unprocessedCount = filteredInquiries.filter(
    item => !item.inquiry.isProcessed && !processedInquiries.has(item.inquiry.question)
  ).length;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[1300] flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">New Inquiries</h3>
            <p className="text-sm text-gray-600 mt-1">
              Total: {filteredInquiries.length} inquiries | Unprocessed: {unprocessedCount}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success/Error Messages */}
        <div className="px-6 pt-4">
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircleIcon className="text-green-600 mr-2" style={{ fontSize: '1.25rem' }} />
              <span className="text-green-800">{success}</span>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Session Filter Dropdown */}
        <div className="px-6 pb-4">
          <label htmlFor="session-filter" className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Session:
          </label>
          <select
            id="session-filter"
            value={selectedSessionId}
            onChange={(e) => setSelectedSessionId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Sessions ({inquiries.length} inquiries)</option>
            {sessionGroups.map(session => (
              <option key={session.sessionId} value={session.sessionId}>
                {session.sessionName} - {session.userEmail} ({session.inquiries.length} inquiries) - {format(new Date(session.callStartTime), 'MMM dd, yyyy')}
              </option>
            ))}
          </select>
        </div>

        {/* Inquiries List */}
        <div id="new-inquiries-modal-content" className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredInquiries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No inquiries found for the selected period
            </div>
          ) : (
            <div className="space-y-4">
              {filteredInquiries.map((item, index) => {
                const isProcessed = item.inquiry.isProcessed || processedInquiries.has(item.inquiry.question);
                const isCurrentlySaving = savingInquiry === item.inquiry.question;

                return (
                  <div
                    key={`${item.sessionId}-${index}`}
                    className={`p-4 rounded-lg border ${
                      isProcessed ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-400'
                    }`}
                  >
                    {/* Session Info */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-500">
                            📅 {format(new Date(item.callStartTime), 'MMM dd, yyyy HH:mm')}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-600">{item.sessionName}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-600">{item.userEmail}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            item.inquiry.category === 'NEW_INQUIRY' || item.inquiry.category === 'New Inquiry'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item.inquiry.category}
                          </span>
                          <span className="text-xs text-gray-500">
                            Relevance: {(item.inquiry.relevance_score * 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isProcessed ? (
                          <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                            ✓ Processed
                          </span>
                        ) : isAdmin ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleMarkAsProcessed(item)}
                              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
                              disabled={isCurrentlySaving}
                            >
                              Mark as Processed
                            </button>
                            <button
                              onClick={() => setShowSaveModal(item)}
                              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                              disabled={isCurrentlySaving}
                            >
                              {isCurrentlySaving ? 'Processing...' : 'Add/Modify Database'}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Question */}
                    <div className="mb-2">
                      <div className="text-sm font-medium text-gray-900">
                        {item.inquiry.question}
                      </div>
                    </div>

                    {/* Answer */}
                    {item.inquiry.answer && (
                      <div className="mt-2 text-sm text-gray-700">
                        <div className="font-medium mb-1">Answer:</div>
                        <div className="pl-3 border-l-2 border-gray-300">
                          {item.inquiry.answer}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Save New Inquiry Modal */}
      {showSaveModal && (
        <SaveInquiryModal
          inquiry={{
            question: showSaveModal.inquiry.question,
            answer: showSaveModal.inquiry.answer,
            category: showSaveModal.inquiry.category
          }}
          onClose={() => setShowSaveModal(null)}
          onSave={(inquiry, type, editedData) => handleSaveNewInquiry(showSaveModal, type, editedData)}
          onMarkAsProcessed={() => handleMarkAsProcessed(showSaveModal)}
          isSaving={savingInquiry === showSaveModal.inquiry.question}
        />
      )}
    </div>
  );
};

export default NewInquiriesModal;
